import React, { Component } from "react";
import {
  loading,
  alert,
  NavbarAdmin,
  format_date,
  GET,
  PUT,
  UPLOAD,
  t,
  can,
} from "../../components/CustomComponent.js";
import { clone, isValidEmail } from "../../components/setting_option.js";
import { setCompany } from "../../services/company.service.js";
import { LogoMark } from "../../components/Logo.js";
import "../../assets/css/property.css";
import "../../assets/css/people.css";
import "../../assets/css/settings.css";

/* ============================================================================
 * ตั้งค่า › ข้อมูลบริษัท  (/ad/settings)
 * ชื่อบริษัท โลโก้ เลขทะเบียน และช่องทางติดต่อ — ข้อมูลชุดนี้ถูกใช้ซ้ำบนหัว
 * เอกสาร ใบเสนอราคา ท้ายประกาศ และเป็นโลโก้ตั้งต้นของลายน้ำรูปภาพ
 * (ตั้งค่าลายน้ำแยกเป็นอีกเมนูที่ /ad/watermark)
 *
 * ต่อกับ API แล้ว :
 *   GET  admin/settings           (Get)
 *   PUT  admin/settings/company   (Save)
 * โลโก้ที่เพิ่งเลือกจากเครื่องถูกอัปขึ้น R2 ตรง ๆ ผ่าน presigned URL ตอนกดบันทึก (UPLOAD)
 * แล้วส่งไปกับ body แค่ URL — ตัวไฟล์ไม่เคยวิ่งผ่าน API ครั้งต่อไปส่ง URL เดิมกลับไป ไม่อัปซ้ำ
 * ========================================================================== */

const LOGO_FOLDER = "company";
const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2MB

const EMPTY_COMPANY = {
  logo: "",
  name_th: "",
  name_en: "",
  tax_id: "",
  license_no: "",
  phone: "",
  hotline: "",
  email: "",
  website: "",
  line_oa: "",
  facebook: "",
  address: "",
};

const isTh = () => (localStorage.getItem("lang") || "TH") !== "EN";
const locale = () => (isTh() ? "th" : "en");

export default class CompanyProfile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: clone(EMPTY_COMPANY),  // ค่าที่กำลังแก้
      saved: clone(EMPTY_COMPANY), // ค่าที่บันทึกไว้ล่าสุด (ไว้เทียบว่ามีแก้ค้างไหม)
      updated_at: null,
      updated_by: "",
    };
    // ไฟล์โลโก้ที่เพิ่งเลือก รออัปขึ้น R2 ตอนกดบันทึก (ไม่เก็บใน state เพราะไม่ต้อง re-render)
    this.logoFile = null;
    this.logoPreview = "";
  }

  componentDidMount() {
    loading(false);
    this.Get();
  }

  componentWillUnmount() {
    this.clearPickedLogo();
  }

  // คืนหน่วยความจำของ object URL ที่สร้างไว้พรีวิว
  clearPickedLogo = () => {
    if (this.logoPreview) URL.revokeObjectURL(this.logoPreview);
    this.logoFile = null;
    this.logoPreview = "";
  };

  /* ------------------------------------------------------------ ดึงค่าตั้งค่า */
  Get = async () => {
    loading(true);
    const result = await GET("admin/settings");
    loading(false);

    if (!result?.status) return;

    this.applyResult(result.data);
  };

  // ใช้ค่าที่ backend ตอบกลับเป็นค่าล่าสุดเสมอ (โลโก้จะกลายเป็น URL หลังบันทึก)
  applyResult = (data) => {
    const company = { ...EMPTY_COMPANY, ...(data?.company || {}) };

    // ไฟล์ที่ค้างอยู่ถูกอัปไปแล้ว (หรือถูกโหลดทับ) ทิ้งพรีวิวชั่วคราวได้
    this.clearPickedLogo();

    /* ส่งเข้าตัวกลางที่ทุกหน้าใช้ร่วมกัน เพื่อให้โลโก้บนเมนู หน้า login และ favicon
       เปลี่ยนตามทันทีที่บันทึก ไม่ต้องรอผู้ใช้รีโหลดหน้า */
    setCompany(company);

    this.setState({
      data: clone(company),
      saved: clone(company),
      updated_at: data?.updated_at || null,
      updated_by: data?.updated_by || "",
    });
  };

  set = (key, value) => this.setState((prev) => ({ data: { ...prev.data, [key]: value } }));

  isDirty = () => JSON.stringify(this.state.data) !== JSON.stringify(this.state.saved);

  /* ---------------------------------------------------------------- โลโก้ */
  // เก็บตัวไฟล์ไว้ก่อน พรีวิวด้วย object URL (ไม่แปลงเป็น base64 เพราะไม่ต้องส่งผ่าน body)
  PickLogo = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_SIZE) return alert("", "warning", t("warning"), t("verify_logo_size"));

    this.clearPickedLogo();
    this.logoFile = file;
    this.logoPreview = URL.createObjectURL(file);
    this.set("logo", this.logoPreview);
  };

  /* ------------------------------------------------------------ บันทึก/คืนค่า */
  Save = async () => {
    const { data } = this.state;

    if (!data.name_th.trim()) return alert("", "warning", t("warning"), t("verify_company_name"));
    if (!isValidEmail(data.email)) return alert("", "warning", t("warning"), t("invalid_email_address"));

    if (!(await alert("", "info", t("confirm"), t("confirm_submission"), t("confirm"), t("cancel")))) return;

    loading(true);

    // อัปโลโก้ขึ้น R2 ก่อน แล้วค่อยส่งเฉพาะ URL ไปกับ body
    // (ค่าใน data.logo ตอนนี้เป็น object URL ของเครื่อง ส่งไป backend ใช้ไม่ได้)
    let payload = data;
    if (this.logoFile) {
      const uploaded = await UPLOAD(this.logoFile, { folder: LOGO_FOLDER });
      if (!uploaded?.status) {
        loading(false);
        return alert("", "warning", t("warning"), `${t("upload_failed")}\n${uploaded?.message || ""}`.trim());
      }
      payload = { ...data, logo: uploaded.data.file_path };

      // ไฟล์ขึ้น R2 แล้ว เก็บ URL จริงแทน object URL ทันที
      // ถ้า PUT ข้างล่างพลาดแล้วผู้ใช้กดบันทึกซ้ำ จะได้ไม่อัปไฟล์เดิมขึ้นไปอีกรอบ
      this.clearPickedLogo();
      this.setState({ data: payload });
    }

    const result = await PUT("admin/settings/company", payload);
    loading(false);

    if (!result?.status) return;

    this.applyResult(result.data);
    alert("", "success", t("success"), t("save_settings_done"));
  };

  Reset = async () => {
    if (!this.isDirty()) return;
    if (!(await alert("", "warning", t("reset_changes"), t("discard_changes_confirm"), t("confirm"), t("cancel")))) return;
    this.clearPickedLogo();
    this.setState((prev) => ({ data: clone(prev.saved) }));
  };

  /* ======================================================= ตัวช่วย render */
  field = (label, control, { required = false, hint = "" } = {}) => (
    <>
      <label className="prop-label">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      {control}
      {!!hint && <div className="ppl-sub mt-1">{hint}</div>}
    </>
  );

  text = (key, props = {}) => (
    <input
      type="text"
      className="prop-field"
      value={this.state.data[key]}
      onChange={(e) => this.set(key, e.target.value)}
      {...props}
    />
  );

  section = (icon, title, desc, children) => (
    <div className="ppl-section">
      <div className="ppl-section__title">
        <span className="icon">{icon}</span>
        {title}
      </div>
      <div className="ppl-section__desc">{desc}</div>
      {children}
    </div>
  );

  /* ================================================ render : การ์ดตัวอย่าง */
  renderPreview = () => {
    const { data } = this.state;

    const line = (icon, value) =>
      !value ? null : (
        <div className="d-flex align-items-start gap-2 text-12" style={{ color: "#47524c" }}>
          <span className="icon text-secondary text-12" style={{ width: "16px", flexShrink: 0 }}>
            {icon}
          </span>
          <span className="text-break">{value}</span>
        </div>
      );

    return (
      <div className="prop-card prop-in" style={{ position: "sticky", top: "82px" }}>
        <div className="prop-card__head">
          <span className="prop-title text-14">
            <span className="prop-title__dot" />
            {t("display_preview")}
          </span>
        </div>

        <div className="p-3">
          <div className="set-brand">
            <div className="set-brand__head">
              {/* ยังไม่ได้อัปโลโก้ = โชว์มาร์กตั้งต้นของระบบ ซึ่งเป็นตัวที่ใช้จริงบนเมนูและหน้า login */}
              <span className="set-brand__logo">
                {data.logo ? <img src={data.logo} alt="logo" /> : <LogoMark size={42} />}
              </span>
              <div className="min-w-0">
                <div className="set-brand__name text-truncate">{data.name_th || t("company_name_th")}</div>
                <div className="set-brand__sub text-truncate">{data.name_en}</div>
              </div>
            </div>

            <div className="d-flex flex-column gap-2">
              {line("\uf095", data.phone)}
              {line("\uf590", data.hotline)}
              {line("\uf0e0", data.email)}
              {line("\uf0ac", data.website)}
              {line("\uf3c5", data.address)}
            </div>

            {!!data.license_no && <div className="set-brand__foot">{`${t("license_no")} ${data.license_no}`}</div>}
          </div>

          <div className="ppl-sub mt-3">{t("display_preview_desc")}</div>
        </div>
      </div>
    );
  };

  render() {
    const { data, updated_at, updated_by } = this.state;
    const dirty = this.isDirty();

    return (
      <div className="w-100 min-vh-100 prop-page">
        <NavbarAdmin />
        <div className="body p-4">
          {/* หัวข้อหน้า */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3 prop-in">
            <div>
              <h3 className="fw-bold mb-1 text-dark">{t("company_profile")}</h3>
              <div className="text-secondary text-14">{t("company_profile_page_desc")}</div>
            </div>
            <div className="text-secondary text-12 text-lg-end">
              {!!updated_at && (
                <div>
                  {t("updated")} {format_date(updated_at, "dd mmm yyyy hh:mm", locale())}
                </div>
              )}
              {!!updated_by && (
                <div>
                  {t("updated_by")} {updated_by}
                </div>
              )}
            </div>
          </div>

          <div className="row g-3">
            {/* ฟอร์ม */}
            <div className="col-xl-8 col-lg-7">
              <div className="prop-card prop-in">
                <div className="p-4">
                  {this.section(
                    "\uf1ad",
                    t("company_profile"),
                    t("company_profile_desc"),
                    <div className="d-flex flex-wrap gap-4">
                      {/* ยังไม่ได้อัปโลโก้ = ช่องนี้ว่าง (data.logo เป็นค่าว่าง ไม่ถูกส่งตอนบันทึก)
                          แต่โชว์มาร์กตั้งต้นให้เห็นว่าตอนนี้ระบบใช้โลโก้ตัวไหนอยู่ */}
                      {/* กล่องโลโก้เป็นสี่เหลี่ยมจัตุรัสและใช้ object-fit: contain
                          รูปไม่ถูกบีบก็จริง แต่ถ้าเล็กเกินจะแตกตอนไปเป็นลายน้ำบนรูปประกาศ
                          จึงบอกขนาดที่แนะนำไว้ใต้กล่องให้เห็นก่อนเลือกไฟล์ */}
                      <div className="d-flex flex-column align-items-center gap-2" style={{ width: "160px", flexShrink: 0 }}>
                        <label className="set-logo">
                          <input type="file" accept="image/*" onChange={this.PickLogo} />
                          {data.logo ? (
                            <img className="set-logo__img" src={data.logo} alt="logo" />
                          ) : (
                            <LogoMark size={78} />
                          )}
                          <span className="set-logo__hint">{t("change_logo")}</span>
                        </label>
                        <div className="ppl-sub text-center">{t("logo_size_hint")}</div>
                      </div>

                      <div className="flex-grow-1" style={{ minWidth: "260px" }}>
                        <div className="row g-3">
                          <div className="col-md-6">
                            {this.field(t("company_name_th"), this.text("name_th"), { required: true })}
                          </div>
                          <div className="col-md-6">{this.field(t("company_name_en"), this.text("name_en"))}</div>
                          <div className="col-md-6">{this.field(t("tax_id"), this.text("tax_id", { maxLength: 13 }))}</div>
                          <div className="col-md-6">
                            {this.field(t("license_no"), this.text("license_no"), { hint: t("license_no_hint") })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {this.section(
                    "\uf095",
                    t("contact_channel"),
                    t("contact_channel_desc"),
                    <div className="row g-3">
                      <div className="col-md-4">{this.field(t("office_phone"), this.text("phone", { maxLength: 10 }))}</div>
                      <div className="col-md-4">{this.field(t("hotline"), this.text("hotline", { maxLength: 10 }))}</div>
                      <div className="col-md-4">{this.field(t("email"), this.text("email"), { required: true })}</div>
                      <div className="col-md-4">{this.field(t("website"), this.text("website"))}</div>
                      <div className="col-md-4">{this.field(t("line_oa"), this.text("line_oa"))}</div>
                      <div className="col-md-4">{this.field(t("facebook_page"), this.text("facebook"))}</div>
                      <div className="col-12">
                        {this.field(
                          t("company_address"),
                          <textarea
                            className="prop-field"
                            rows={3}
                            style={{ height: "auto", padding: "10px 12px" }}
                            value={data.address}
                            onChange={(e) => this.set("address", e.target.value)}
                          />,
                          { hint: t("company_address_hint") }
                        )}
                      </div>
                    </div>
                  )}

                  <div className="ppl-note mt-4">
                    <span className="icon">{"\uf05a"}</span>
                    <div>{t("company_logo_watermark_hint")}</div>
                  </div>
                </div>

                {/* แถบบันทึก — ตรึงท้ายกล่องให้กดได้ทันทีแม้ฟอร์มยาว */}
                {/* ไม่มีสิทธิ์แก้ = ไม่ต้องมีแถบบันทึก ดูค่าที่ตั้งไว้ได้อย่างเดียว */}
                {can("settings.update") && (
                  <div className="ppl-savebar">
                    {dirty ? (
                      <span className="ppl-dirty">
                        <span className="ppl-dirty__dot" />
                        {t("unsaved_changes")}
                      </span>
                    ) : (
                      <span className="text-secondary text-12 d-flex align-items-center gap-2">
                        <span className="icon">{"\uf058"}</span>
                        {t("all_changes_saved")}
                      </span>
                    )}

                    <div className="d-flex gap-2">
                      <button type="button" className="prop-btn prop-btn--ghost" disabled={!dirty} onClick={this.Reset}>
                        <span className="icon text-12">{"\uf0e2"}</span>
                        {t("reset_changes")}
                      </button>
                      <button type="button" className="prop-btn" disabled={!dirty} onClick={this.Save}>
                        <span className="icon text-12">{"\uf0c7"}</span>
                        {t("save_settings")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ตัวอย่างการแสดงผล */}
            <div className="col-xl-4 col-lg-5">{this.renderPreview()}</div>
          </div>
        </div>
      </div>
    );
  }
}
